import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

export type LayoutMode = 'top-nav' | 'sidebar';
export type ThemePreset = 'modern' | 'classic' | 'custom';
export type SidebarMode = 'expanded' | 'collapsed' | 'mini';

interface LayoutPreferences {
  layoutMode: LayoutMode;
  themePreset: ThemePreset;
  sidebarMode: SidebarMode;
  sidebarWidth: number;
  miniSidebarWidth: number;
  customColors?: {
    primary: string;
    secondary: string;
    background: string;
    paper: string;
  };
  customFonts?: {
    heading: string;
    body: string;
  };
  iconStyle?: 'rounded' | 'sharp' | 'outlined';
}

interface LayoutContextType {
  preferences: LayoutPreferences;
  setLayoutMode: (mode: LayoutMode) => void;
  setThemePreset: (preset: ThemePreset) => void;
  setSidebarMode: (mode: SidebarMode) => void;
  updatePreferences: (updates: Partial<LayoutPreferences>) => void;
  resetToDefaults: () => void;
}

const defaultPreferences: LayoutPreferences = {
  layoutMode: 'sidebar',  // Sidebar as default for CEO
  themePreset: 'classic',  // Classic theme as default
  sidebarMode: 'expanded',
  sidebarWidth: 240,
  miniSidebarWidth: 72,
  iconStyle: 'rounded',
};

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};

interface LayoutProviderProps {
  children: React.ReactNode;
}

const STORAGE_KEY = 'itcp-layout-preferences';

export const LayoutProvider: React.FC<LayoutProviderProps> = ({ children }) => {
  const getInitialPreferences = (): LayoutPreferences => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...defaultPreferences, ...parsed };
      }
    } catch (error) {
      console.error('Failed to load layout preferences:', error);
    }
    return defaultPreferences;
  };

  const [preferences, setPreferences] = useState<LayoutPreferences>(getInitialPreferences);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (error) {
      console.error('Failed to save layout preferences:', error);
    }
  }, [preferences]);

  const setLayoutMode = (mode: LayoutMode) => {
    setPreferences(prev => ({ ...prev, layoutMode: mode }));
  };

  const setThemePreset = (preset: ThemePreset) => {
    setPreferences(prev => ({ ...prev, themePreset: preset }));
  };

  const setSidebarMode = (mode: SidebarMode) => {
    setPreferences(prev => ({ ...prev, sidebarMode: mode }));
  };

  const updatePreferences = (updates: Partial<LayoutPreferences>) => {
    setPreferences(prev => ({ ...prev, ...updates }));
  };

  const resetToDefaults = () => {
    setPreferences(defaultPreferences);
    localStorage.removeItem(STORAGE_KEY);
  };

  const contextValue = useMemo(
    () => ({
      preferences,
      setLayoutMode,
      setThemePreset,
      setSidebarMode,
      updatePreferences,
      resetToDefaults,
    }),
    [preferences]
  );

  return (
    <LayoutContext.Provider value={contextValue}>
      {children}
    </LayoutContext.Provider>
  );
};